import React from 'react';

// import Hint from '@components/basics/hint';
import BoxHeader from '@components/layout/box-header';
import SocialNetworkInputCtrl, { Platforms } from '../social-network-input';
import MotionOpacity from '@components/motion/motion-opacity';
import styled from 'styled-components';
import { TracPageSocialLink } from '@services/tracpage/types/track-page-social-link';
import { SOCIAL_NETWORK_LIST } from '@common/types/data/social-network';
import {
  arrayMove,
  SortableContainer,
  SortEnd,
  SortableElement,
} from 'react-sortable-hoc';
import SortableHandleButton from '@components/basics/sortable-button';
import { Translate } from 'react-localize-redux';
import { ErrorMessage } from '@components/basics/error-message';
import { useDebouncedCallback } from 'use-debounce';
import { checkStartWith } from '@webapp/controllers/form/streaming-platform-input';
import platformValidationString from '@webapp/controllers/form/streaming-platform-input';

interface SocialNetworkListCtrlProps {
  className?: string;
  includeStreaming?: boolean;
  networks: TracPageSocialLink[];
  onChange: (value: TracPageSocialLink[]) => void;
}

const NETWORKS_PROVIDERS: TracPageSocialLink['provider'][] =
  SOCIAL_NETWORK_LIST.sort();

const SortableSocialLinkItem = SortableElement(
  ({
    link,
    withHandle,
    includeStreaming,
    handleChange,
    handleRemove,
  }: {
    withHandle: boolean;
    link: TracPageSocialLink;
    includeStreaming?: boolean;
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageSocialLink>(
      order: number,
      field: T,
      value: string,
      error?: string | null
    ) => void;
  }) => {
    return (
      <>
        <StyledMotionOpacity
          key={`social-network-${link.order}`}
          className="social-network-input"
        >
          <SocialNetworkInputCtrl
            error={link.error}
            network={link.provider}
            value={link.handle || link.url}
            onNetworkChange={(value) =>
              handleChange<'provider'>(link.order, 'provider', value as string)
            }
            onChange={(value: string, error?: string | null) =>
              handleChange<'url'>(link.order, 'url', value, error)
            }
            onClose={() => {
              handleRemove(link.order);
              return false;
            }}
            includeStreaming={includeStreaming}
          />
          {withHandle && <SortableHandleButton />}
        </StyledMotionOpacity>
        <ErrorMessage
          className="mt-10"
          errorId={
            link.error ? 'ARTIST_PROFILE.SETTINGS.SOCIAL.ERROR' : undefined
          }
          message={link.error}
        />
      </>
    );
  }
);

const SortableSocialLinks = SortableContainer(
  ({
    links,
    handleChange,
    handleRemove,
    includeStreaming,
  }: {
    links: TracPageSocialLink[];
    includeStreaming?: boolean;
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageSocialLink>(
      order: number,
      field: T,
      value: string
    ) => void;
  }) => {
    return (
      <div>
        {links.map((link, i) => (
          <SortableSocialLinkItem
            link={link}
            index={i}
            includeStreaming={includeStreaming}
            key={`${link.order}-${link.provider}`}
            withHandle={links.length > 1}
            handleChange={handleChange}
            handleRemove={handleRemove}
          />
        ))}
      </div>
    );
  }
);

const SocialNetworkListCtrl: React.FC<SocialNetworkListCtrlProps> = ({
  networks,
  onChange,
  ...props
}) => {
  const [newLinkValue, setNewLinkValue] = React.useState('');
  const [newLinkError, setNewLinkError] = React.useState('');
  const [newLinkProvider, setNewLinkProvider] = React.useState<Platforms>('');

  const handleChangeValue = (value: string, error?: string | null) => {
    setNewLinkValue(value);
    // tslint:disable-next-line:no-console
    console.log('error: ', error); // eslint-disable-line no-console
  };

  const handleChange = <T extends keyof TracPageSocialLink>(
    order: number,
    key: T,
    value: string,
    error?: string | null
  ) => {
    onChange(
      networks
        .map((network) => {
          if (network.order === order) {
            if (key === 'url') {
              return {
                ...network,
                [key]: value,
                error: error,
              };
            }
            return {
              ...network,
              [key]: value,
            };
          }

          return network;
        })
        .filter(
          (link, index, arr) =>
            arr.findIndex((v) => v.provider === link.provider) === index
        )
    );
  };

  const handleAddMore = useDebouncedCallback(
    (value: string, network: string) => {
      const networksProviders = networks.reduce((acc: string[], network) => {
        acc.push(network.provider);
        return acc;
      }, []);
      const nextProvider = NETWORKS_PROVIDERS.filter(
        (provider) => !networksProviders.includes(provider)
      ).find((provider) => provider === network);
      if (nextProvider) {
        const newNetwork: TracPageSocialLink[] = [
          {
            provider: nextProvider,
            url: value,
            error: null,
            order: 0,
          },
        ];
        onChange(
          newNetwork
            .concat(
              networks.map((link, i) => ({
                ...link,
                order: (link.order || i) + 1,
              }))
            )
            .filter(
              (link, index, arr) =>
                arr.findIndex((v) => v.provider === link.provider) === index
            )
        );
        setNewLinkValue('');
        setNewLinkProvider('');
      }
    },
    500
  );

  React.useEffect(() => {
    if (newLinkValue && newLinkProvider) {
      if (
        !checkStartWith(platformValidationString[newLinkProvider], newLinkValue)
      ) {
        setNewLinkError(platformValidationString[newLinkProvider]);
        return;
      }
      setNewLinkError('');
      handleAddMore(newLinkValue, newLinkProvider);
    }

    if (!newLinkValue || !newLinkProvider) {
      setNewLinkError('');
    }
  }, [newLinkValue, newLinkProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemove = (order: number) => {
    onChange(getNewOrder(networks.filter((net) => net.order !== order)));
  };

  const getNewOrder = (items: TracPageSocialLink[]) => {
    return items.map((item, order) => {
      return {
        ...item,
        order,
      };
    });
  };

  const updateLinksOrder = ({ oldIndex, newIndex }: SortEnd) => {
    const newLinks = arrayMove(networks, oldIndex, newIndex);
    const ordered = getNewOrder(newLinks);
    onChange(ordered);
  };

  return (
    <StyledSocialNetworkListCtrl className={props.className}>
      <BoxHeader
        title={<Translate id="ARTIST_PROFILE.SETTINGS.SOCIAL.TITLE" />}
        text={<Translate id="ARTIST_PROFILE.SETTINGS.SOCIAL.SUBTITLE" />}
        // hasButton
        noMargin
        buttonClose={false}
      />
      {/* TODO: DEVTEAM-845 - Integrate adding a new element from this component and added to the sortable list */}
      <SocialNetworkInputCtrl
        className="mt-10"
        error={newLinkError}
        network={newLinkProvider}
        value={newLinkValue}
        onNetworkChange={setNewLinkProvider}
        onChange={handleChangeValue}
        includeStreaming={false}
        placeholder={'Add new social profile'}
        hideMenu
      />
      <ErrorMessage
        className="mt-10"
        errorId={
          newLinkError ? 'ARTIST_PROFILE.SETTINGS.SOCIAL.ERROR' : undefined
        }
        message={newLinkError}
      />
      <SortableSocialLinks
        links={networks}
        handleChange={handleChange}
        handleRemove={handleRemove}
        lockAxis="y"
        useDragHandle
        helperClass="sorting"
        lockToContainerEdges={true}
        includeStreaming={false}
        onSortEnd={updateLinksOrder}
      />
    </StyledSocialNetworkListCtrl>
  );
};

export default SocialNetworkListCtrl;

const StyledSocialNetworkListCtrl = styled.div`
  .social-network-input {
    margin-top: 10px;
    &:first-child {
      margin-top: 30px;
    }
  }
`;

const StyledMotionOpacity = styled(MotionOpacity)`
  position: relative;

  .decoration-left {
    left: 24px;
  }
`;
